/**
 * VERA-246 — DEV-ONLY tag-shop mock data.
 *
 * Gated behind VITE_TAG_SHOP_MOCK=true. Lets the whole Shop suite
 * (storefront → product → cart → checkout-through-shipping → orders) be
 * verified in the preview while the live Shopify store is unavailable.
 *
 * VRG-156: the catalog is derived from the canonical TAG_CATALOG (7 form-factor
 * products, each with a selectable Capability ladder × Size × Quantity variant grid,
 * provisional pricing) — so the mocked shop matches the real model + the Shopify
 * seed. NEVER enabled in production builds.
 */
import type {TagShopProduct, TagShopProductsResponse} from 'hooks/api/useTagProducts'
import type {TagShopCalculateResult} from 'services/tagShopApi'
import type {TagShopOrderItem} from 'hooks/api/useTagOrders'
import {TAG_CATALOG, primaryTagImage, type CatalogProduct} from 'constants/tagCatalog'

export const TAG_SHOP_MOCK_ENABLED =
  import.meta.env.VITE_TAG_SHOP_MOCK === 'true'

// ─── Catalog (derived from TAG_CATALOG, VRG-143) ─────────────────────────────

const stripHtml = (html: string) => html.replace(/<[^>]+>/g, '').trim()

/**
 * The picture for a form: the real render when one has been shot, picsum when not.
 *
 * ⚠️ THIS USED TO BE picsum FOR EVERY FORM, and the reason to change it is not
 * polish. A mock whose images are random stock photographs cannot show whether
 * the shop's layout works for the actual product — eight black tags on
 * transparency against a light card is a very different grid from eight
 * full-bleed landscape photographs, and every spacing and contrast judgement made
 * against the latter was made against the wrong picture.
 *
 * ⚠️ THE FILES ARE NOT IN THE REPO, AND MUST NOT BE. `tag-imagery/` is gitignored
 * (see .gitignore, VRG-266): layers live in GCS and the local directory is a
 * staging area. `TAG_IMAGERY_ROOT` falls back to the relative `/tag-imagery`, so
 * this resolves against `apps/platform/public/tag-imagery/tag/` on a machine
 * where someone has staged the shots, and 404s on one where nobody has.
 *
 * ⚠️ A 404 IS A BROKEN IMAGE, NOT A FALLBACK — this function cannot detect one,
 * because existence is a runtime fact about the network and this is a pure
 * module. So a checkout with no staged shots shows six broken tiles and two
 * picsum ones. That is the honest failure and it is survivable in a DEV-ONLY
 * mock; do not "fix" it by reverting to picsum, which would hide the real
 * pictures from everyone who does have them. Stage the files:
 *
 *   gsutil -m rsync -r gs://vera-tag-imagery-dev apps/platform/public/tag-imagery
 *
 * ⚠️ PICSUM REMAINS FOR FabricLabel AND ClosureLoop, WHICH HAVE NO RENDER. Deliberately
 * an obvious placeholder rather than a neighbouring form's photograph — see
 * PHOTOGRAPHED_SIZE. A stock photo reads as "not done"; a Fabric Patch labelled
 * Fabric Label reads as done and is wrong.
 */
function formImage(p: CatalogProduct): string {
  return primaryTagImage(p.form) ?? `https://picsum.photos/seed/veratag-${p.form.toLowerCase()}/600`
}

function catalogToTagShopProduct(p: CatalogProduct): TagShopProduct {
  /* ⚠️ ONE IMAGE FOR THE PRODUCT AND ALL ITS VARIANTS, FOR NOW. The model
     supports per-variant art — `resolveVariantLayers(key, quantity)` — and that
     is the correct end state. It is not wired here because only ONE size per
     form has been photographed, so resolving per variant would 404 on every
     variant except the one, replacing eight good pictures with ~280 broken
     ones. Switch when the shot library is complete, not before. */
  const img = formImage(p)
  const startingUnit = Math.min(...p.variants.map(v => v.unit_price))
  return {
    id: p.handle,
    sku: p.handle, // route + lookup key (the product axis is form; variants carry the real SKUs)
    title: p.title,
    images: [img],
    best_for_product_types: p.best_for_product_types,
    shopify_category_id: null,
    category_type: 'Tags',
    description: stripHtml(p.descriptionHtml),
    details: p.capabilities.join(' · '),
    width: null,
    height: null,
    depth: null,
    // logo-canvas outline: rectangle if a rect shape is offered, else round.
    shape: p.shapes.some(s => /rect/i.test(s)) ? 'rect' : p.shapes.length ? 'round' : null,
    sides: p.sides,
    unit_price: startingUnit,
    variants: p.variants.map((v, i) => ({
      id: `${v.sku}-v${i}`,
      sku: v.sku,
      title: `${v.capability} · ${v.size} · Case of ${v.quantity}`,
      quantity: v.quantity,
      price: v.price,
      unit_price: v.unit_price,
      images: [img],
      capability: v.capability,
      size: v.size,
      tag_family: v.tag_family,
    })),
    form_factor: p.form,
    capabilities: p.capabilities,
    capabilityLeaves: p.capabilityLeaves,
    on_metal_available: p.on_metal_available,
    shapes: p.shapes,
    sizes: p.sizes,
    durability: p.durability ?? null,
    embroidery: p.embroidery ?? null,
    cardMaterial: p.cardMaterial ?? null,
  }
}

export const mockTagProducts: TagShopProduct[] = TAG_CATALOG.map(catalogToTagShopProduct)

export const mockTagProductsResponse: TagShopProductsResponse = {
  products: mockTagProducts,
  next_cursor: undefined,
}

export function getMockTagProduct(sku?: string): TagShopProduct | undefined {
  if (!sku) return undefined
  return (
    mockTagProducts.find(p => p.sku === sku) ??
    mockTagProducts.find(p => p.variants.some(v => v.sku === sku))
  )
}

// ─── Checkout calculate (Shopify-authoritative tax + shipping stand-in) ───────

export const mockCalculateResult: TagShopCalculateResult = {
  subtotalPrice: 1234,
  totalTax: 98.72,
  totalShippingPrice: 15,
  totalPrice: 1347.72,
  availableShippingRates: [
    {handle: 'standard', title: 'Standard (5–7 business days)', price: 15},
    {handle: 'expedited', title: 'Expedited (2–3 business days)', price: 35},
    {handle: 'overnight', title: 'Overnight', price: 65},
  ],
}

// ─── Orders (independent historical mock for the Orders tab) ──────────────────

export const mockTagOrders: TagShopOrderItem[] = [
  {
    id: '5001',
    name: '#VERA-1042',
    createdAt: '2026-06-20T15:12:00.000Z',
    totalPriceSet: {shopMoney: {amount: '1347.72', currencyCode: 'USD'}},
    fulfillmentStatus: 'fulfilled',
    financialStatus: 'paid',
    customer: {firstName: 'Spencer', lastName: 'Stebbins', email: 'sjfstebbins@gmail.com'},
    shippingAddress: {address1: '123 Market St', city: 'San Francisco', country: 'US', zip: '94103'},
    lineItems: [
      {id: 'li1', title: 'Vera Sticker Tag', quantity: 2, productId: 'vera-tag-sticker',
        variant: {id: 'VERA-STK-VER-30-100-v0', sku: 'VERA-STK-VER-30-100', price: '120', title: 'Verify · 30mm · Case of 100'}},
      {id: 'li2', title: 'Vera Fabric Patch', quantity: 1, productId: 'vera-tag-fabricpatch',
        variant: {id: 'VERA-FPT-VER-25-250-v1', sku: 'VERA-FPT-VER-25-250', price: '410', title: 'Verify · 25mm · Case of 250'}},
    ],
    tags: ['tag-shop'],
    note: 'Payment captured by Vera: pi_mock_1042',
    trackingNumber: '1Z999AA10123456784',
    trackingUrl: 'https://www.ups.com/track?tracknum=1Z999AA10123456784',
    trackingCompany: 'UPS',
    shipmentStatus: 'IN_TRANSIT',
    estimatedDeliveryAt: '2026-07-02T00:00:00.000Z',
  },
  {
    id: '5002',
    name: '#VERA-1039',
    createdAt: '2026-06-11T09:03:00.000Z',
    totalPriceSet: {shopMoney: {amount: '500.00', currencyCode: 'USD'}},
    fulfillmentStatus: 'unfulfilled',
    financialStatus: 'paid',
    customer: {firstName: 'Spencer', lastName: 'Stebbins', email: 'sjfstebbins@gmail.com'},
    shippingAddress: {address1: '123 Market St', city: 'San Francisco', country: 'US', zip: '94103'},
    lineItems: [
      {id: 'li3', title: 'Vera Sticker Tag', quantity: 1, productId: 'vera-tag-sticker',
        variant: {id: 'VERA-STK-VER-30-500-v2', sku: 'VERA-STK-VER-30-500', price: '500', title: 'Verify · 30mm · Case of 500'}},
    ],
    tags: ['tag-shop'],
    note: '',
    trackingNumber: '',
    trackingUrl: '',
    trackingCompany: '',
    shipmentStatus: '',
    estimatedDeliveryAt: null,
  },
]

export function getMockTagOrder(id?: string): TagShopOrderItem | null {
  if (!id) return null
  return mockTagOrders.find(o => o.id === id) ?? mockTagOrders[0]
}
